import { config } from '@abyss/web/tools/config';
import { storage } from '@abyss/web/tools/storage';
import {
  QueryParamsKeys,
  SessionInitStatus,
  StoreKeys,
} from '@/common/constants';
import {
  EmptySSOPortalEligibilityData,
  SSOPortalEligibilityData,
} from 'api/src/dataModels/SSOPortalEligibilityData';
import { useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { createSsoSession } from 'api/src/services/createSsoSession';
import { SSO_SESSION_COOKIE_NAME } from 'commonUtils/src/sessionManager';
import { INTERNAL_ERROR_PATH } from '../../appConstants';

export const useSessionInit = (
  searchParams,
  router
): keyof typeof SessionInitStatus => {
  const refToken = searchParams?.get('REF');

  // TODO: only allow below params if DEMO feature flag from lagoon is true.
  const portalSource = searchParams?.get(QueryParamsKeys.PORTAL_SOURCE);
  const portalIdentifier = searchParams?.get(QueryParamsKeys.PORTAL_IDENTIFIER);
  const useFakeUser = searchParams?.get('useFakeUser') === 'true';

  const [ssoPortalEligibilityData, setSsoPortalEligibilityData] =
    useSessionStorage<SSOPortalEligibilityData>(
      StoreKeys.SSO_PORTAL_ELIGIBILITY_DATA,
      EmptySSOPortalEligibilityData
    );

  const [initStatus, setInitStatus] = useState<keyof typeof SessionInitStatus>(
    SessionInitStatus.NOT_INITIALIZED
  );

  useEffect(() => {
    const doCreateSession = async (
      params
    ): Promise<SSOPortalEligibilityData> => {
      try {
        const data: SSOPortalEligibilityData = await createSsoSession(
          params,
          config
        );

        if (portalSource) data.portalSource = portalSource;
        if (portalIdentifier) data.portalIdentifier = portalIdentifier;

        return data;
      } catch (error) {
        throw error;
      }
    };

    const doInit = async () => {
      if (refToken || useFakeUser) {
        const refCodeForStoring = refToken || 'fakeUserToken';

        // check if we already made the init request for the same ref token
        const existingRefToken = storage.session.get(
          StoreKeys.EXISTING_REF_TOKEN
        );
        const needToCreateSession = refCodeForStoring !== existingRefToken;
        if (!needToCreateSession) {
          if (ssoPortalEligibilityData.firstName) {
            setInitStatus(SessionInitStatus.SUCCESS);
          }
        } else {
          setSsoPortalEligibilityData(EmptySSOPortalEligibilityData);
          storage.session.set(StoreKeys.EXISTING_REF_TOKEN, refCodeForStoring);

          let params = {};
          if (refToken) params = { refToken };
          if (useFakeUser) params = { useFakeUser };
          try {
            const data = await doCreateSession(params);
            setSsoPortalEligibilityData(data);
            setInitStatus(SessionInitStatus.SUCCESS);
          } catch (error) {
            if (error?.status >= 400 && error?.status < 600) {
              setInitStatus(SessionInitStatus.ERROR);
              router?.navigate(INTERNAL_ERROR_PATH);
            }
          }
        }
      } else {
        if (ssoPortalEligibilityData.firstName) {
          setInitStatus(SessionInitStatus.SUCCESS);
        } else if (storage.cookie.get(SSO_SESSION_COOKIE_NAME)) {
          // If cookie exists, call createSession. If session is still valid, we will get SSO attributes
          try {
            const data = await doCreateSession({});
            setSsoPortalEligibilityData(data);
            setInitStatus(SessionInitStatus.SUCCESS);
          } catch (error) {
            if (error?.status >= 400 && error?.status < 600) {
              setInitStatus(SessionInitStatus.ERROR);
              router?.navigate(INTERNAL_ERROR_PATH);
            }
          }
        }
      }
    };

    doInit();
  }, [
    portalIdentifier,
    portalSource,
    refToken,
    setSsoPortalEligibilityData,
    ssoPortalEligibilityData,
    useFakeUser,
    router,
  ]);

  return initStatus;
};
